import { type RefObject, useEffect, useLayoutEffect, useState } from 'react'

const OFFSETS_CHANGED = 'sticky-offsets-changed'

// Publishes an element's height as a CSS variable on `host` so sticky rows below it
// can offset by it (e.g. a stacked sub-header's `top`).
export function useStickyHeightVar(
  ref: RefObject<HTMLElement | null>,
  name: string,
  host: (el: HTMLElement) => HTMLElement | null,
  mounted = true,
) {
  useLayoutEffect(() => {
    const el = ref.current
    const target = el && host(el)
    if (!el || !target) return
    const measure = () => {
      target.style.setProperty(name, `${el.getBoundingClientRect().height}px`)
      window.dispatchEvent(new Event(OFFSETS_CHANGED))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => {
      ro.disconnect()
      target.style.removeProperty(name)
    }
  }, [ref, name, host, mounted])
}

// True while a position: sticky element is pinned at its `top`. An observer whose
// root edge sits 1px below that offset sees the pinned element as clipped.
export function useStuck(ref: RefObject<HTMLElement | null>, mounted = true) {
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let io: IntersectionObserver | undefined
    const observe = () => {
      io?.disconnect()
      const top = parseFloat(getComputedStyle(el).top) || 0
      io = new IntersectionObserver(
        ([e]) =>
          setStuck(
            e.isIntersecting && e.intersectionRatio < 1 && e.boundingClientRect.top <= top + 1,
          ),
        { rootMargin: `${-Math.ceil(top) - 1}px 0px 0px 0px`, threshold: [0, 1] },
      )
      io.observe(el)
    }
    let frame = 0
    const reobserve = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(observe)
    }
    observe()
    window.addEventListener('resize', reobserve)
    window.addEventListener(OFFSETS_CHANGED, reobserve)
    return () => {
      cancelAnimationFrame(frame)
      io?.disconnect()
      window.removeEventListener('resize', reobserve)
      window.removeEventListener(OFFSETS_CHANGED, reobserve)
    }
  }, [ref, mounted])
  return stuck
}
